import { Card, CardContent, TableCell, Typography } from "@mui/material";
import type { z } from "zod";
import { omitNullFields } from "../../../components/Form";
import Link from "../../../components/Link";
import { LogInfo } from "../../../components/ResourceInfo";
import type { Column } from "../../../components/Table";
import {
  dateTimeColumns,
  getActiveFiltersCount,
  makeRequestSchema,
  prepareListRequest,
  ResourceTable,
  useSearchRequest,
  withoutBaseTableModel,
} from "../../../components/Table";
import { useExtractions } from "../../../domain/crud";
import {
  makeExtractionLocation,
  makeNewExtractionLocation,
} from "../../../paths";
import type { Extraction } from "../../../services/datastore";
import { ExtractionFilters } from "./ExtractionFilters";
import { listExtractionsSchema } from "./validation";

const COLUMNS: ReadonlyArray<Column<Extraction>> = [
  {
    header: "Extraction",
    renderCell(extraction) {
      return (
        <TableCell>
          {extraction.name}
          <Link to={makeExtractionLocation(extraction.id)}>
            <Typography variant="body2">{extraction.id}</Typography>
          </Link>
        </TableCell>
      );
    },
  },
  {
    header: "Log",
    renderCell(extraction) {
      return (
        <TableCell>
          <LogInfo logId={extraction.logId} />
        </TableCell>
      );
    },
  },
  {
    accessor: "status",
    align: "right",
    isSortable: true,
  },
  {
    accessor: "format",
    isSortable: true,
  },
  {
    accessor: "s3Bucket",
    header: "S3 Bucket",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "s3Key",
    header: "S3 Key",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "size",
    dataType: "bytes",
    isSortable: true,
  },
  {
    accessor: "progress",
    dataType: "percent",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "note",
    isSortable: true,
  },
  ...dateTimeColumns,
];

const requestSchema = makeRequestSchema(COLUMNS, listExtractionsSchema);

export default function Table() {
  const [request, setRequest] = useSearchRequest(requestSchema);
  const searchQuery = useExtractionSearch(request);

  const filterValues = withoutBaseTableModel(request);

  return (
    <Card>
      <CardContent>
        <ResourceTable
          resourceName="extraction"
          resourceCreateLocation={makeNewExtractionLocation()}
          getRowKey={(extraction) => extraction.id}
          columns={COLUMNS}
          searchQuery={searchQuery}
          tableModel={request}
          onTableModelChange={setRequest}
          filterSection={
            <ExtractionFilters values={filterValues} setValues={setRequest} />
          }
          activeFilterCount={getActiveFiltersCount(filterValues)}
        />
      </CardContent>
    </Card>
  );
}

function useExtractionSearch(request: z.infer<typeof requestSchema>) {
  return useExtractions(omitNullFields(prepareListRequest(request)), {
    keepPreviousData: true,
    cacheTime: 0,
  });
}
